import { useMemo, useState } from "react";
import { X } from "lucide-react";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

/**
 * Tags, with the library's own suggested as you type.
 *
 * Suggesting matters more than it looks: the library has 38 speciality tags
 * built up over six years, and someone typing "photography" where everyone
 * else wrote "Photographer" splits a shelf in two. Free text is still allowed —
 * refusing a new tag would freeze the vocabulary at whatever 2019 happened to
 * need.
 */
export function TagInput({
  value,
  onChange,
  suggestions,
  placeholder = "Add a tag…",
}: {
  value: string[];
  onChange: (tags: string[]) => void;
  suggestions: string[];
  placeholder?: string;
}) {
  const [draft, setDraft] = useState("");

  const matches = useMemo(() => {
    const typed = draft.trim().toLowerCase();
    if (!typed) return [];
    return suggestions
      .filter((s) => s.toLowerCase().includes(typed) && !value.includes(s))
      .slice(0, 6);
  }, [draft, suggestions, value]);

  function add(tag: string) {
    const clean = tag.trim();
    if (!clean || value.includes(clean)) return;
    onChange([...value, clean]);
    setDraft("");
  }

  return (
    <div className="space-y-1.5">
      {value.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {value.map((tag) => (
            <span
              key={tag}
              className="bg-secondary text-secondary-foreground inline-flex items-center gap-1 rounded-md px-2 py-0.5 text-xs"
            >
              {tag}
              <button
                type="button"
                aria-label={`Remove ${tag}`}
                onClick={() => onChange(value.filter((t) => t !== tag))}
              >
                <X className="size-3 opacity-60 hover:opacity-100" />
              </button>
            </span>
          ))}
        </div>
      )}
      <Input
        value={draft}
        placeholder={placeholder}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === ",") {
            e.preventDefault();
            // The first suggestion wins on Enter, so the common case — typing
            // most of an existing tag — lands on the existing one rather than
            // creating a near-duplicate.
            add(matches[0] ?? draft);
          } else if (e.key === "Backspace" && !draft && value.length) {
            onChange(value.slice(0, -1));
          }
        }}
      />
      {matches.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {matches.map((s) => (
            <button
              key={s}
              type="button"
              onClick={() => add(s)}
              className={cn(
                "border-border text-muted-foreground hover:text-foreground hover:bg-muted",
                "rounded-md border px-2 py-0.5 text-xs"
              )}
            >
              {s}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
